import React, { useEffect, useContext, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Page from "../components/Page";
import { AuthContext } from "../providers/AuthProvider";
import API from "../api";
import StandingsView from "../components/StandingsView";
import { Button } from "react-bootstrap";
import Loader from "../components/Loader";
import BackButton from "../components/BackButton";
import ScrollToTop from "../components/ScrollToTop";
import CreatePrivateLeague from "../modals/CreatePrivateLeague";
import { Dropdown } from "react-bootstrap";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { getLeagueInviteUrl } from "../modals/CreatePrivateLeague";
import Message from "../components/Message";
import { SettingsContext } from "../providers/SettingsProvider";
import useFetchSettings from "../hooks/useFetchSettings";
import { isPwa } from "./Home";
import ClientCache from "../cache";

const filters = [
  {
    type: "SEASON",
    label: "Сезон",
  },
  {
    type: "MONTH",
    label: "Місяць",
  },
  {
    type: "PREV_ROUND",
    label: "Тур",
  },
  {
    type: "LIVE",
    label: "LIVE",
  },
];

const getFilterLabel = (type) => {
  return filters.find((filter) => filter.type === type)?.label;
};

const getFilterParameter = (selectedFilter, monthIndex, currentRound) => {
  switch (selectedFilter) {
    case "MONTH":
      return monthIndex;
    case "PREV_ROUND":
      return currentRound - 1;
    case "LIVE":
      return currentRound;
    default:
      return null;
  }
};

const PrivateLeagues = () => {
  const { leagueId } = useParams();
  const { user, setUser } = useContext(AuthContext);
  const {
    monthIndex,
    currentRound,
    activeDeadline,
    roundDeadline,
  } = useContext(SettingsContext);

  const [standingItems, setStandingItems] = useState([]);
  const [createLeagueModal, setCreateLeagueModal] = useState(false);
  const [loadedItems, setLoadedItems] = useState(false);

  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingLeagues, setLoadingLeagues] = useState(true);

  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [isAddBtnVisible, setIsAddBtnVisible] = useState(false);

  const navigate = useNavigate();

  useFetchSettings({ page: "private-league" });

  useEffect(() => {
    if (!roundDeadline) return;
    const liveFilter = filters[3].type;
    const seasonFilter = filters[0].type;
    if (activeDeadline) {
      setSelectedFilter(seasonFilter);
    } else {
      setSelectedFilter(liveFilter);
    }
  }, [activeDeadline, roundDeadline]);

  const fetchLeague = useCallback(
    async (privateLeagueId, selectedFilter) => {
      const filter = getFilterParameter(
        selectedFilter,
        monthIndex,
        currentRound
      );

      const clientCache = ClientCache.getInstance();
      const cacheKey = `${privateLeagueId}_${filter}`;
      const cachedItems = clientCache.get(cacheKey);

      try {
        if (cachedItems !== null) {
          setStandingItems(cachedItems);
        } else {
          setLoadingItems(true);
          const res = await API.fetchLeague({
            currentRound,
            privateLeagueId,
            filter,
          });

          setStandingItems(res.data);
          clientCache.set(cacheKey, res.data);
        }
      } catch (err) {
        console.log(err);
      } finally {
        setLoadedItems(true);
        setLoadingItems(false);
      }
    },
    [currentRound, monthIndex]
  );

  const onLeagueSelect = (item) => {
    navigate(`/private-leagues/${item.LeagueId}`);
  };

  useEffect(() => {
    if (!user?.me) return;
    const { privateLeagues } = user?.me;
    if (privateLeagues.length > 0) {
      setLeagues(privateLeagues);
      setSelectedLeague(
        privateLeagues.find((pl) => pl.LeagueId === leagueId) ??
          privateLeagues[0]
      );
    } else {
      setLeagues([]);
      setSelectedLeague(null);
    }

    setIsAddBtnVisible(
      privateLeagues.filter((league) => league.OwnerId === user?.me?.userId)
        .length < 3
    );

    setLoadingLeagues(false);
  }, [leagueId, user]);

  useEffect(() => {
    if (!selectedLeague) {
      setStandingItems([]);
      return;
    }
    fetchLeague(selectedLeague?.LeagueId, selectedFilter);
  }, [selectedLeague, selectedFilter, fetchLeague]);

  const showModal = () => {
    setCreateLeagueModal(true);
  };

  const hideModal = (createdLeague) => {
    if (createdLeague !== null) {
      setUser({
        ...user,
        me: {
          ...user.me,
          privateLeagues: [...user.me.privateLeagues, createdLeague],
        },
      });
      navigate(`/private-leagues/${createdLeague.LeagueId}`);
    }
    setCreateLeagueModal(false);
  };

  console.log(user);

  const leaveLeague = async () => {
    const result = window.confirm(
      `Ви впевнені в тому, що хочете припинити участь у лізі ${selectedLeague.LeagueTitle}?`
    );
    if (result) {
      setLoadingLeagues(true);
      await API.updatePrivateLeague({
        leagueId: selectedLeague.LeagueId,
        leave: true,
      });
      setUser({
        ...user,
        me: {
          ...user.me,
          privateLeagues: user.me.privateLeagues.filter(
            (pl) => pl.LeagueId !== selectedLeague.LeagueId
          ),
        },
      });
      navigate(`/private-leagues`);
    }
  };

  const onTrashClick = async () => {
    const result = window.confirm(
      `Ви впевнені в тому, що хочете видалити лігу ${selectedLeague.LeagueTitle}?`
    );
    if (result) {
      setLoadingLeagues(true);

      await API.deletePrivateLeague(selectedLeague.LeagueId);
      setUser({
        ...user,
        me: {
          ...user.me,
          privateLeagues: user.me.privateLeagues.filter(
            (pl) => pl.LeagueId !== selectedLeague.LeagueId
          ),
        },
      });
      navigate("/private-leagues");
    }
  };

  const filterDropdown = (
    <Dropdown className="private-league-filter">
      <Dropdown.Toggle
        disabled={!monthIndex || !currentRound}
        variant={selectedFilter === "LIVE" ? "danger" : "success"}
        className="private-league-filter-toggle"
      >
        {getFilterLabel(selectedFilter)}
      </Dropdown.Toggle>

      <Dropdown.Menu style={{ minWidth: 76 }}>
        {filters
          .filter((filter) => filter.type !== selectedFilter)
          .map((filter) => (
            <Dropdown.Item
              key={filter["type"]}
              onClick={() => setSelectedFilter(filter["type"])}
            >
              {filter["label"]}
            </Dropdown.Item>
          ))}
      </Dropdown.Menu>
    </Dropdown>
  );

  const actionButtons =
    selectedLeague?.OwnerId === user?.me?.userId ? (
      <div className="private-league-actions">
        <CopyToClipboard
          text={getLeagueInviteUrl(selectedLeague?.LeagueId)}
          onCopy={() => alert("Лінк-запрошення скопійовано до буферу обміну")}
        >
          <Button
            style={{ fontSize: "1.1rem", marginLeft: 8 }}
            variant="warning"
            size="sm"
          >
            <i className="bi bi-share"></i>
          </Button>
        </CopyToClipboard>
        <Button
          onClick={onTrashClick}
          style={{ marginLeft: 8, fontSize: "1.1rem" }}
          variant="danger"
          size="sm"
        >
          <i className="bi bi-trash lightText"></i>
        </Button>
      </div>
    ) : (
      <div className="private-league-actions">
        <Button
          style={{ fontSize: "1.3rem" }}
          onClick={leaveLeague}
          variant="danger"
          size="sm"
        >
          <i className="bi bi-box-arrow-left lightText"></i>
        </Button>
      </div>
    );

  return (
    <Page>
      <ScrollToTop />
      <div className="page-bar">
        <div
          className="inner"
          style={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div className="page-header">
            <div className="page-header-left">
              <BackButton />
              {leagues.length > 0 && (
                <Dropdown>
                  <Dropdown.Toggle
                    disabled={leagues.length < 2}
                    variant="warning"
                    className="private-league-dropdown"
                  >
                    <span className="private-league-title-text">
                      {selectedLeague?.LeagueTitle}
                    </span>
                  </Dropdown.Toggle>

                  <Dropdown.Menu style={{ minWidth: 76 }}>
                    {leagues.map(
                      (item, index) =>
                        selectedLeague.LeagueId !== item.LeagueId && (
                          <Dropdown.Item
                            key={index}
                            onClick={() => onLeagueSelect(item)}
                          >
                            {item.LeagueTitle}
                          </Dropdown.Item>
                        )
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          </div>
          <div style={{ display: "flex" }}>
            {leagues.length > 0 && (
              <div className="private-league-title">
                {selectedLeague?.OwnerId === user?.me?.userId ? (
                  <div className="private-league-actions">{filterDropdown}</div>
                ) : (
                  <div className="private-league-actions">{filterDropdown}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {loadingItems ? (
        <Loader />
      ) : loadedItems ? (
        <StandingsView items={standingItems} />
      ) : (
        <></>
      )}

      {!loadingItems &&
        !loadedItems &&
        !loadingLeagues &&
        leagues.length === 0 && (
          <div className="page-offset">
            <Message text={"У вас поки що немає приватних ліг"} />
          </div>
        )}
      <CreatePrivateLeague show={createLeagueModal} onHide={hideModal} />
      <div className="private-league-admin">
        <div className={`private-league-admin-cnt ${isPwa() ? "pwa" : ""}`}>
          <>
            <Button
              size="sm"
              onClick={showModal}
              variant="primary"
              style={{
                fontSize: "1.1rem",
                visibility: isAddBtnVisible ? "visible" : "hidden",
              }}
            >
              <i className="bi bi-plus-circle-fill"></i>&nbsp;НОВА ЛІГА
            </Button>
          </>
          {selectedLeague !== null && actionButtons}
        </div>
      </div>
    </Page>
  );
};

export default PrivateLeagues;
